import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog';
import { Input } from '@/components/shadcn/input';
import showToast from '@/components/ui/Toast';
import whatsappCommunityService, {
  toQrImageSrc,
} from '@/services/whatsappCommunityService';

const QR_TTL_SECONDS = 60;
const POLL_MS = 3000;

interface ConnectWhatsappModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultDeviceName: string;
  onConnected: () => void;
  qrTtlSeconds?: number;
}

function formatTimer(seconds: number): string {
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

const ConnectWhatsappModal = ({
  isOpen,
  onClose,
  defaultDeviceName,
  onConnected,
  qrTtlSeconds = QR_TTL_SECONDS,
}: ConnectWhatsappModalProps) => {
  const [deviceName, setDeviceName] = useState(defaultDeviceName);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(false);
  const creatingRef = useRef(false);
  const ttl = Math.max(1, qrTtlSeconds);

  useEffect(() => {
    if (isOpen) setDeviceName(defaultDeviceName);
  }, [isOpen, defaultDeviceName]);
  const startQr = useCallback(async () => {
    if (creatingRef.current) return;
    creatingRef.current = true;
    setLoading(true);
    setExpired(false);
    try {
      const created = await whatsappCommunityService.createSession(deviceName);
      setQrCode(created.qrCode || null);
      setSessionId(created.sessionId || null);
      setSecondsLeft(ttl);
      setExpired(false);
    } catch (error) {
      showToast.error(error instanceof Error ? error.message : 'Gagal memuat QR WhatsApp');
      setQrCode(null);
      setSessionId(null);
      setSecondsLeft(0);
      setExpired(true);
    } finally {
      setLoading(false);
      creatingRef.current = false;
    }
  }, [deviceName, ttl]);

  useEffect(() => {
    if (!isOpen) {
      setQrCode(null);
      setSessionId(null);
      setSecondsLeft(0);
      setExpired(false);
      setLoading(false);
      creatingRef.current = false;
      return;
    }
    void startQr();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps -- only on open

  useEffect(() => {
    if (!isOpen || expired || secondsLeft <= 0) return undefined;
    const id = window.setTimeout(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          setExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearTimeout(id);
  }, [isOpen, expired, secondsLeft]);

  useEffect(() => {
    if (!isOpen || expired || !sessionId) return undefined;
    const id = window.setInterval(async () => {
      try {
        const sessions = await whatsappCommunityService.listSessions();
        const matched = sessions.find(
          (s) => s.session_id === sessionId && s.status === 'active'
        );
        if (matched) {
          showToast.success('WhatsApp berhasil terhubung');
          onConnected();
          onClose();
        }
      } catch {
        // keep polling
      }
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [isOpen, expired, sessionId, onConnected, onClose]);

  const qrSrc = toQrImageSrc(qrCode);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto rounded-2xl p-5 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-left text-lg font-semibold text-[#0E1726]">
            Hubungkan WhatsApp
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <div className="relative mx-auto flex h-52 w-52 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white sm:h-56 sm:w-56">
            {loading && !qrSrc ? (
              <p className="text-sm text-gray-500">Memuat QR...</p>
            ) : qrSrc ? (
              <img src={qrSrc} alt="QR WhatsApp" className="h-full w-full object-contain p-2" />
            ) : (
              <p className="px-4 text-center text-sm text-gray-500">QR belum tersedia</p>
            )}

            {expired && (
              <button
                type="button"
                onClick={() => void startQr()}
                className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/85"
                aria-label="Muat ulang QR"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-primary-500 text-primary-500">
                  <Icon icon="mdi:refresh" className="h-7 w-7" />
                </span>
                <span className="text-sm font-semibold text-gray-900">QR kedaluwarsa</span>
                <span className="text-xs text-gray-500">Muat ulang untuk QR baru</span>
              </button>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="wa-device-name" className="text-sm font-medium text-gray-800">
              Nama kontak
            </label>
            <Input
              id="wa-device-name"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="Nama komunitas"
              className="h-11 rounded-xl"
            />
          </div>

          <ol className="space-y-3 text-sm text-gray-600">
            {[
              'Buka WhatsApp di ponsel, lalu masuk ke Setelan > Perangkat tertaut.',
              'Ketuk Tautkan perangkat.',
              'Arahkan kamera ke QR code ini. QR berlaku 60 detik.',
            ].map((text, index) => (
              <li key={text} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-white">
                  {index + 1}
                </span>
                <span className="leading-relaxed">{text}</span>
              </li>
            ))}
          </ol>

          <div className="flex items-center gap-2 text-sm text-red-500">
            <Icon icon="mdi:timer-outline" className="h-5 w-5 shrink-0" />
            {expired || secondsLeft <= 0 ? (
              <span>00:00 QR sudah tidak berlaku</span>
            ) : (
              <span>{formatTimer(secondsLeft)} sampai QR kedaluwarsa</span>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConnectWhatsappModal;
